import type { ChartHistoryFilterType } from "@roman/shared/constants/predictionChart";
import type { ChartProps, Choice } from "@roman/shared/features/chart/types";
import type { PredictionMarketDetailsResponse } from "@roman/shared/hooks/PredictionMarket/useGetPredictionMarketDetails";
import type { PredictionMarketType } from "@roman/shared/utils/predictionMarket/utils";
import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import type { SWRConfiguration } from "swr";
import { useChartData } from "../hooks/useChartData";
import type { ChartConfig, ChartDataPoint } from "../types";

interface ChartContextValue {
  chartData: ChartDataPoint[];
  selectedChoices: Choice[];
  selectedChoiceIds: string[];
  setSelectedChoiceIds: (ids: string[]) => void;
  chartConfig: ChartConfig;
  colorsByChoiceId: Record<string, string>;
  displayMode: ChartHistoryFilterType;
  setDisplayMode: (mode: ChartHistoryFilterType) => void;
  isLineVisible?: boolean;
  marketType: PredictionMarketType | null;
  sortedChoices: Choice[];
  predictionMarketId?: string;
  isWebViewLoaded: boolean;
  setIsWebViewLoaded: (loaded: boolean) => void;
  marketEndTime: number | null | undefined;
  rawHistoryData?: PredictionMarketDetailsResponse;
}

export const ChartContext = createContext<ChartContextValue | null>(null);

interface ChartProviderProps extends ChartProps {
  children: ReactNode;
  swrOptions?: SWRConfiguration;
}

export const ChartProvider = ({ children, swrOptions, ...props }: ChartProviderProps) => {
  const [internalIsLineVisible, setInternalIsLineVisible] = useState(false);
  const [isWebViewLoaded, setIsWebViewLoaded] = useState(false);
  const [displayMode, setDisplayMode] = useState<ChartHistoryFilterType>(props.displayMode || "all");

  const {
    chartData,
    selectedChoices,
    selectedChoiceIds,
    setSelectedChoiceIds,
    chartConfig,
    colorsByChoiceId,
    marketType,
    sortedChoices,
    isLoadingChartData,
    marketEndTime,
    rawHistoryData,
  } = useChartData({
    predictionMarketId: props.predictionMarketId,
    defaultShowChartIds: props.defaultShowChartIds,
    predictionMarketDetails: props.predictionMarketDetails,
    displayMode,
    swrOptions,
  });

  useEffect(() => {
    if (!props.displayMode) return;
    setDisplayMode(props.displayMode);
  }, [props.displayMode]);

  useEffect(() => {
    if (!isLoadingChartData && chartData.length > 0) {
      const timer = setTimeout(() => {
        setInternalIsLineVisible(true);
      }, 1);
      return () => clearTimeout(timer);
    }
  }, [isLoadingChartData, chartData.length]);

  const value: ChartContextValue = {
    chartData,
    selectedChoices,
    selectedChoiceIds,
    setSelectedChoiceIds,
    chartConfig,
    colorsByChoiceId,
    displayMode,
    setDisplayMode,
    isLineVisible: internalIsLineVisible,
    marketType,
    sortedChoices,
    predictionMarketId: props.predictionMarketId,
    isWebViewLoaded,
    setIsWebViewLoaded,
    marketEndTime,
    rawHistoryData,
  };

  return <ChartContext.Provider value={value}>{children}</ChartContext.Provider>;
};

export const useChartContext = () => {
  const context = useContext(ChartContext);
  if (!context) {
    throw new Error("useChartContext must be used within a ChartProvider");
  }
  return context;
};
